import dayjs, {Dayjs} from "dayjs";
import {
    IProtocolStep,
    TProtocolStepId,
} from "@/protocols/types/protocol.ts";
import {IProtocolStepEvent, TProtocolStepEventId} from "@/protocols/types/IProtocolStepEvent.ts";
import {TimingWarning, isEventOutsideStepBoundary} from "./checkStepTiming";

/**
 * Represents an item in the timeline - either a step or an event.
 */
export type TimelineItem =
    | { type: "step"; step: IProtocolStep; timingWarning?: TimingWarning }
    | { type: "orphan-events"; events: IProtocolStepEvent[] };

/**
 * Events associated with a step, sorted by timestamp.
 */
interface StepEvents {
    events: IProtocolStepEvent[];
    hasWarning: boolean;
    warningMessage?: string;
}

/**
 * Result from interleaveEvents containing timeline items and event warning info.
 */
export interface InterleaveResult {
    /** Timeline items (steps + grouped orphan events) */
    timeline: TimelineItem[];
    /** Map of eventId -> warning message for events outside step boundaries */
    eventWarnings: Map<TProtocolStepEventId, string>;
    /** Orphan events that couldn't be definitively placed (placed at end of timeline) */
    unplacedOrphanEvents: IProtocolStepEvent[];
}

/**
 * Helper to sort events by timestamp.
 */
function sortEventsByTime(events: IProtocolStepEvent[]): IProtocolStepEvent[] {
    return [...events].sort((a, b) =>
        dayjs(a.event.startTime).valueOf() - dayjs(b.event.startTime).valueOf()
    );
}

/**
 * Binary search to find the gap index for an event.
 * 
 * gapIndex = count of step start times that are strictly before eventTime
 * 
 * Examples (steps with start times at 10:00, 11:00, 12:00):
 *   - Event at 09:00 -> gapIndex = 0 (before first step)
 *   - Event at 10:15 -> gapIndex = 1 (between step at 10:00 and step at 11:00)
 *   - Event at 11:30 -> gapIndex = 2 (between step at 11:00 and step at 12:00)
 *   - Event at 12:01 -> gapIndex = 3 (after last step)
 * 
 * @param eventTime - The event's timestamp
 * @param stepStartTimes - Sorted array of step start times
 * @returns gap index (0 to len(stepStartTimes))
 */
function binarySearchGap(eventTime: Dayjs, stepStartTimes: Dayjs[]): number {
    let left = 0;
    let right = stepStartTimes.length;

    while (left < right) {
        const mid = Math.floor((left + right) / 2);
        if (stepStartTimes[mid].isBefore(eventTime)) {
            left = mid + 1;
        } else {
            right = mid;
        }
    }

    return left; // left is count of start times strictly before eventTime
}

/**
 * Interleaves events into the protocol timeline based on timestamps.
 * 
 * Events with protocolStepId are NOT added as separate timeline items - they are
 * intended to be rendered by StepRow using getStepEvents(). Only orphan events
 * (no protocolStepId) are inserted into the timeline as grouped items.
 * 
 * Events that fall outside their parent step's time boundaries are flagged with warnings.
 * 
 * Algorithm (event-centric with binary search):
 * 1. Filter steps to only those with startTime (these define gap boundaries)
 * 2. Check for overlapping step times and log warnings
 * 3. For each orphan event, use binary search to find gap index
 * 4. Group events by gap index
 * 5. Build timeline by merging steps with gap groups
 * 6. Events that can't be definitively placed go to unplacedOrphanEvents (at end)
 * 
 * @param orderedSteps - Steps in natural protocol order
 * @param events - All protocol step events
 * @param stepTimingWarnings - Warnings from checkStepTimingWarnings (optional)
 * @returns InterleaveResult with timeline items, event warnings, and unplaced orphan events
 */
export function interleaveEvents(
    orderedSteps: IProtocolStep[],
    events: IProtocolStepEvent[],
    stepTimingWarnings?: Map<TProtocolStepId, TimingWarning>
): InterleaveResult {
    // Map to store warning messages per event ID
    const eventWarnings = new Map<TProtocolStepEventId, string>();
    const unplacedOrphanEvents: IProtocolStepEvent[] = [];

    if (orderedSteps.length === 0) {
        // No steps - return all orphan events as a single grouped item
        const orphanEvents = events.filter(e => !e.protocolStepId);
        return {
            timeline: orphanEvents.length > 0
                ? [{type: "orphan-events", events: sortEventsByTime(orphanEvents)}]
                : [],
            eventWarnings,
            unplacedOrphanEvents: []
        };
    }

    // A. Check for overlapping step times
    for (let i = 1; i < orderedSteps.length; i++) {
        const prev = orderedSteps[i - 1];
        const curr = orderedSteps[i];

        // Check for overlapping times: current step starts before previous step ends
        if (prev.endTime && curr.startTime && curr.startTime.isBefore(prev.endTime)) {
            console.warn(
                `[interleaveEvents] Step times overlap: step ${i - 1} ends at ${prev.endTime.format("HH:mm:ss")} but step ${i} starts at ${curr.startTime.format("HH:mm:ss")}`
            );
        }
    }

    // B. Build arrays of steps with startTime (for gap boundary calculation)
    const stepsWithStartTime: IProtocolStep[] = [];
    const stepStartTimes: Dayjs[] = [];

    for (const step of orderedSteps) {
        if (step.startTime) {
            stepsWithStartTime.push(step);
            stepStartTimes.push(step.startTime);
        }
    }

    // Build a map of stepId -> events for events with protocolStepId
    const eventsByStepId = new Map<TProtocolStepId, StepEvents>();

    // Sort all events by timestamp first
    const sortedEvents = [...events].sort((a, b) => {
        const aTime = dayjs(a.event.startTime);
        const bTime = dayjs(b.event.startTime);
        return aTime.valueOf() - bTime.valueOf();
    });

    for (const protocolEvent of sortedEvents) {
        if (protocolEvent.protocolStepId) {
            // Event belongs to a step - store in map but do NOT add to timeline
            const stepId = protocolEvent.protocolStepId;

            if (!eventsByStepId.has(stepId)) {
                eventsByStepId.set(stepId, {events: [], hasWarning: false});
            }

            const stepEvents = eventsByStepId.get(stepId)!;
            stepEvents.events.push(protocolEvent);

            // Check if event timestamp is outside step boundaries
            const parentStep = orderedSteps.find(s => s.id === stepId);
            if (parentStep) {
                const eventTime = dayjs(protocolEvent.event.startTime);
                const outsideBoundary = isEventOutsideStepBoundary(
                    eventTime,
                    parentStep.startTime,
                    parentStep.endTime
                );

                if (outsideBoundary) {
                    stepEvents.hasWarning = true;
                    const stepStart = parentStep.startTime?.format("HH:mm:ss") ?? "unset";
                    const stepEnd = parentStep.endTime?.format("HH:mm:ss") ?? "unset";
                    const warningMsg = `Event at ${eventTime.format("HH:mm:ss")} falls outside step boundaries [${stepStart}, ${stepEnd}]`;
                    eventWarnings.set(protocolEvent.id, warningMsg);
                }
            }
        }
        // Orphan events will be handled in the event-centric pass below
    }

    // C & D. Event-centric approach: assign orphan events to gaps using binary search
    const gapMap = new Map<number, IProtocolStepEvent[]>();
    const orphanEvents = sortedEvents.filter(e => !e.protocolStepId);

    // If no steps have startTime, all orphan events are unplaced
    if (stepStartTimes.length === 0) {
        return {
            timeline: orderedSteps.map(step => ({
                type: "step" as const,
                step,
                timingWarning: stepTimingWarnings?.get(step.id)
            })),
            eventWarnings,
            unplacedOrphanEvents: sortEventsByTime(orphanEvents)
        };
    }

    for (const event of orphanEvents) {
        const eventTime = dayjs(event.event.startTime);
        const gapIndex = binarySearchGap(eventTime, stepStartTimes);

        if (!gapMap.has(gapIndex)) {
            gapMap.set(gapIndex, []);
        }
        gapMap.get(gapIndex)!.push(event);
    }

    // E. Build timeline by merging steps with gap groups
    const timeline: TimelineItem[] = [];

    for (let i = 0; i < orderedSteps.length; i++) {
        const step = orderedSteps[i];

        // Find the gap index for this step in stepsWithStartTime
        // (to know which gap group to insert before this step)
        const stepGapIndex = stepsWithStartTime.indexOf(step);

        // If this step has a startTime, it defines a gap boundary
        // Insert orphan events for the gap BEFORE this step (if any)
        if (stepGapIndex >= 0 && gapMap.has(stepGapIndex)) {
            const eventsForGap = sortEventsByTime(gapMap.get(stepGapIndex)!);
            timeline.push({type: "orphan-events", events: eventsForGap});
        }

        // Add the step itself with its timing warning if any
        // Note: Events with protocolStepId are NOT added here - they are rendered via StepRow using getStepEvents()
        const timingWarning = stepTimingWarnings?.get(step.id);
        timeline.push({
            type: "step",
            step,
            timingWarning
        });
    }

    // After last step: add any remaining gap events (gapIndex == stepsWithStartTime.length)
    if (gapMap.has(stepsWithStartTime.length)) {
        const eventsForGap = sortEventsByTime(gapMap.get(stepsWithStartTime.length)!);
        timeline.push({type: "orphan-events", events: eventsForGap});
    }

    return {timeline, eventWarnings, unplacedOrphanEvents};
}